/**
 * Кастомный селект. Единый компонент для всего фронта.
 * Dropdown рендерится через портал, чтобы не обрезаться родителями с overflow: auto/hidden.
 */
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import '../../styles/components/custom-select.css'

const LOADING_VALUE = '__loading__'

function providerLabel(provider) {
  if (provider === 'grok') return 'Grok'
  if (provider === 'groq') return 'Groq'
  if (provider === 'openrouter') return 'OpenRouter'
  if (provider === 'minimax') return 'MiniMax'
  if (provider === 'openai') return 'OpenAI'
  return null
}

export default function Select({
  options,
  value,
  onChange,
  placeholder = 'Выбрать…',
  className = '',
  dropdownPosition = 'up',
  onOpen,
  /** Dropdown расширяется по контенту, чтобы длинные опции влезали в одну строку */
  dropdownAutoWidth = false,
}) {
  const [open, setOpen] = useState(false)
  const [dropdownRect, setDropdownRect] = useState(null)
  const containerRef = useRef(null)
  const listRef = useRef(null)
  const closedByOutsideRef = useRef(false)

  const valueStr = value == null ? '' : String(value)
  const selected = options.find((o) => String(o.value) === valueStr)
  const label = selected ? selected.label : placeholder

  function handleOpen() {
    if (closedByOutsideRef.current) {
      closedByOutsideRef.current = false
      return
    }
    if (!open) {
      onOpen?.()
    }
    setOpen((prev) => !prev)
  }

  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setDropdownRect({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
      })
    } else {
      setDropdownRect(null)
    }
  }, [open])

  // Обновление позиции dropdown при скролле (dropdown следует за триггером)
  useEffect(() => {
    if (!open || !containerRef.current) return
    let rafId
    let prevRect = null
    const update = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const changed =
        !prevRect ||
        prevRect.top !== rect.top ||
        prevRect.left !== rect.left ||
        prevRect.width !== rect.width ||
        prevRect.bottom !== rect.bottom
      if (changed) {
        prevRect = { ...rect }
        setDropdownRect({ top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width })
      }
      rafId = requestAnimationFrame(update)
    }
    rafId = requestAnimationFrame(update)
    return () => cancelAnimationFrame(rafId)
  }, [open])

  useEffect(() => {
    function handleOutside(e) {
      const target = e.target
      if (!target || !document.contains(target)) return
      if (listRef.current?.contains(target)) return
      if (containerRef.current?.contains(target)) {
        if (open) {
          closedByOutsideRef.current = true
          setOpen(false)
          e.stopPropagation()
          e.preventDefault()
        }
        return
      }
      setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', handleOutside, true)
      return () => document.removeEventListener('mousedown', handleOutside, true)
    }
  }, [open])

  function handleSelect(opt) {
    if (opt.value === LOADING_VALUE) return
    onChange(opt.value)
    setOpen(false)
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') setOpen(false)
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (!open) onOpen?.()
      setOpen((prev) => !prev)
    }
  }

  const dropdownContent = open && dropdownRect && (
    <ul
      ref={listRef}
      className={`custom-select-dropdown custom-select-dropdown--${dropdownPosition} custom-select-dropdown--portal`}
      role="listbox"
      tabIndex={-1}
      style={{
        position: 'fixed',
        left: dropdownRect.left,
        ...(dropdownAutoWidth
          ? { minWidth: dropdownRect.width, width: 'max-content' }
          : { width: dropdownRect.width }),
        ...(dropdownPosition === 'down'
          ? { top: dropdownRect.bottom + 4 }
          : { bottom: window.innerHeight - dropdownRect.top + 4 }),
      }}
    >
      {options.map((opt) => {
        const providerPrefix = providerLabel(opt.provider)
        const levelBadge = ['l1', 'l2', 'l3', 'l4'].includes(opt.level) ? opt.level.toUpperCase() : null
        const suffix = providerPrefix ? opt.label.replace(new RegExp(`^${providerPrefix}\\s+`), '') : opt.label
        const displayLabel = opt.label
        return (
          <li
            key={opt.value}
            role="option"
            aria-selected={String(opt.value) === valueStr}
            aria-disabled={opt.value === LOADING_VALUE}
            className={`custom-select-option ${String(opt.value) === valueStr ? 'custom-select-option--selected' : ''} ${opt.value === LOADING_VALUE ? 'custom-select-option--loading' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleSelect(opt)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleSelect(opt)
              }
            }}
          >
            {levelBadge ? (
              <>
                <span className={`variable-level-badge variable-level-badge--${opt.level}`}>{levelBadge}</span>
                {displayLabel}
              </>
            ) : providerPrefix ? (
              <>
                <span className={`custom-select-option-prefix custom-select-option-prefix--${opt.provider}`}>{providerPrefix}</span>
                {' '}{suffix}
              </>
            ) : (
              opt.label
            )}
          </li>
        )
      })}
    </ul>
  )

  return (
    <div
      ref={containerRef}
      className={`custom-select ui-select ${open ? 'custom-select--open' : ''} ${className}`.trim()}
    >
      <button
        type="button"
        className="custom-select-trigger"
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        <span className="custom-select-value">
          {selected?.level && ['l1', 'l2', 'l3', 'l4'].includes(selected.level) ? (
            <>
              <span className={`variable-level-badge variable-level-badge--${selected.level}`}>
                {selected.level.toUpperCase()}
              </span>
              {selected.label}
            </>
          ) : selected?.provider ? (
            <>
              <span className={`custom-select-option-prefix custom-select-option-prefix--${selected.provider}`}>
                {providerLabel(selected.provider) || selected.provider}
              </span>
              {' '}{selected.label.replace(/^(OpenAI|Grok|Groq|OpenRouter|MiniMax)\s+/, '')}
            </>
          ) : (
            label
          )}
        </span>
        <span className="custom-select-chevron" aria-hidden>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  )
}
